import React, { useMemo } from "react";
import { Col, Row } from "reactstrap";
import { Link } from "react-router-dom";
import { Component as Form } from "@repoflow/form-schema";
import ContentTabs from "@repoflow/components/content-tabs";
import { useSectionContext } from "@repoflow/components/section";
import { usePreview } from "@repoflow/components/preview";
import * as widgets from "widgets/register";

import { useEditorPage } from "../../../context";
import { findSection, sectionOptions, sectionPatch } from "../../../sections";
import * as ConfigRoute from "../../config";

// A `section` element: a pointer at a section of this site.
//
// There is one field, and the tab exists so that choosing it does not mean
// scrolling past the frame and the tags. The link beside it is what gen-2 had
// and is the point of a section — the content is edited in one place and every
// page carrying it changes.
const Select = () => {
  const { schema, state, attributes } = useSectionContext();
  const { sections } = useEditorPage();
  const preview = usePreview();
  const { values, setValues } = state;
  const config = values.config || {};

  const options = useMemo(() => sectionOptions(sections), [sections]);

  const formConfig = useMemo(
    () => ({
      "/Page/Element/Type/Section": {
        fields: [{ property: "sectionId", widget: "select", options }],
      },
    }),
    [options]
  );

  return (
    <Row>
      <Col>
        <Form
          schema={schema}
          target={{ $ref: "/Page/Element/Type/Section" }}
          widgets={widgets}
          values={config}
          onChange={(mod) => {
            // Repointing an element at a different section copies that
            // section's frame and tags onto it, exactly as choosing one in the
            // add modal does — see ../../../sections for what is copied and
            // what is left alone. The two pickers are the same decision made at
            // two moments, so they cannot differ.
            //
            // The copy is done HERE rather than through a `config.onChange`
            // cascade like the modal's, because this form's values ARE the
            // branch: `setValues` already holds the whole element, so there is
            // no sibling to reach for.
            const section =
              mod.sectionId !== config.sectionId
                ? findSection(sections, mod.sectionId)
                : undefined;

            setValues({
              ...values,
              ...(section ? sectionPatch(values, section) : null),
              config: mod,
            });

            // The page's own values changed, so the preview would reload
            // anyway — but the content it has to fetch changed too, and the
            // reload is what re-fetches. Asking for it explicitly is what makes
            // picking a section feel immediate rather than debounced.
            preview.refresh();
          }}
          config={formConfig}
        />

        {config.sectionId ? (
          <Link to={`/site/${attributes.siteId}/section/${config.sectionId}/edit`}>
            Edit this section
          </Link>
        ) : null}
      </Col>
    </Row>
  );
};

export const Content = () => {
  const tabs = useMemo(
    () => [
      { label: "Select", content: <Select /> },
      { label: "Element", content: <ConfigRoute.Content /> },
    ],
    []
  );

  return <ContentTabs tabs={tabs} />;
};
